import { useEffect, useMemo, useRef, useState } from 'react';
import { navigate } from '../../app/navigation';
import { aiBackendConfigured } from '../../backend/aiClient';
import { isInspectionLocked, type V2Inspection } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { aiFailureMessage, areaDisplayName } from '../../i18n/labels';
import { aiPolicyBlockFor, aiStore, aiUnavailableReason, houseContextOf } from '../../state/aiStore';
import { aiQuotaBlockFor, billingStore, ensureBillingStarted } from '../../state/billingStore';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { findingStore } from '../../state/findingStore';
import { areasOf, inspectionStore } from '../../state/inspectionStore';
import { poolAiCandidates, poolAiFiling, poolAiLiveOf, poolAiResultOf, poolAiStore, poolAiUncertainLeft, poolAreaFindings, retryPoolAreasAction, runPoolAnalysisAction, type PoolAiLive } from '../../state/poolAiStore';
import { poolAiEstimateSeconds, poolAiTiming, readPoolAiSpeed } from '../../state/poolAiTiming';
import { sessionStore } from '../../state/sessionStore';
import { useBackHandler } from '../../ui/backStack';
import { Button, Card, Icon, Portal, SectionEyebrow } from '../../ui/components';
import { useOnlineStatus } from '../../ui/hooks';
import { useOverlayTouchGuard } from '../../ui/overlayTouch';
import { useBodyScrollLock } from '../../ui/scrollLock';
import { AiBlockFix } from '../ai/AiBlockFix';
import { PurchaseSheet } from '../billing/PurchaseSheet';

// 「AI 分析全部照片」 (Owner device test 2026-10-05) — the entry, the live progress and the outcome of the
// whole-pool analysis (src/state/poolAiStore.ts), shown on the processing choice and in the photo pool.
//   - Before it starts, when any photo has no note, a dialog says that notes on every photo make the AI
//     analysis better, with 现在去添加 (back to the pool, where every photo takes a note) and 直接 AI 分析;
//   - before it starts: about how long it takes (on the card and in the reminder);
//   - while it runs: what it is doing, the percentage, a bar that moves every second, the time used and
//     the time left (Owner 2026-10-18: 「6分钟很久…别让用户以为是机子卡主」);
//   - when it is done: where the photos went, area by area (each opens the area to review and edit),
//     the photos it could not place, and a retry for an area whose analysis did not finish.
// LAYER 1 (PP-21): the button is computed from the policy gate and the allowance like every AI entry —
// disabled with the real reason, never a silent failure.
export function PoolAiCard(props: {
  inspection: V2Inspection;
  // 现在去添加: where the inspector writes the notes (the pool's grid of the photos to analyze).
  onAddNotes: () => void;
  // compact = the one-line form inside the photo pool's sticky footer.
  compact?: boolean;
  // compact only: the outcome is shown by the full card; this opens it.
  onShowResult?: () => void;
  // Called when a run starts (the pool goes back to its index, where the progress is).
  onStarted?: () => void;
  testId?: string;
}) {
  const { t, tp, locale } = useT();
  const online = useOnlineStatus();
  const sessionPhase = useStore(sessionStore, (s) => s.phase);
  const evidenceState = useStore(evidenceStore, (s) => s);
  const inspectionState = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const aiState = useStore(aiStore, (s) => s);
  const live = useStore(poolAiStore, (s) => poolAiLiveOf(s, props.inspection.id));
  const result = useStore(poolAiStore, (s) => poolAiResultOf(s, props.inspection.id));
  const candidates = useMemo(() => poolAiCandidates(props.inspection.id, evidenceState, inspectionState, findingState), [props.inspection.id, evidenceState, inspectionState, findingState]);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const policyBlock = aiPolicyBlockFor(props.inspection);
  const quotaBlock = useStore(billingStore, () => aiQuotaBlockFor(props.inspection.id));
  const canBuyAddOn = useStore(billingStore, (s) => s.canBuyAddOn);
  useEffect(() => {
    void ensureBillingStarted();
  }, []);
  const locked = isInspectionLocked(props.inspection);
  const unavailable = aiUnavailableReason({ photos: candidates.length, online, signedIn: sessionPhase === 'signedIn', configured: aiBackendConfigured(), locked, busy: live !== null, policyBlock, quotaBlock });
  const missingNotes = candidates.filter((e) => e.caption.trim().length === 0).length;
  const areasAvailable = areasOf(inspectionState, props.inspection.id).filter((a) => a.enabled).length;
  // 2026-10-19 (Owner's 8 points, #5): with the heating fuel not recorded the model asks "is this an oil tank?"
  // in every area it sees one (and the same for the other systems). Said once here, before the run, with the
  // way to the facts — whenever the heating fuel, the fact behind most of those questions, is still blank.
  const house = houseContextOf(props.inspection, inspectionState.items.filter((i) => i.inspectionId === props.inspection.id));
  const houseFactsMissing = house === null || house.heatingFuel === null;
  const areaById = new Map(inspectionState.areas.map((a) => [a.id, a] as const));
  const areaName = (areaId: string) => {
    const area = areaById.get(areaId);
    return area ? areaDisplayName(locale, area) : '';
  };

  function requestStart() {
    if (missingNotes > 0) setReminderOpen(true);
    else void start();
  }

  async function start() {
    setReminderOpen(false);
    props.onStarted?.();
    await runPoolAnalysisAction(props.inspection.id);
  }

  function addNotes() {
    setReminderOpen(false);
    props.onAddNotes();
  }

  const reason =
    unavailable === null ? null : candidates.length === 0 && unavailable === 'noPhotos' ? t('poolAi.nothing') : aiFailureMessage(t, unavailable);

  const startButton = (
    <Button variant={result && !props.compact ? 'secondary' : 'primary'} onClick={requestStart} disabled={unavailable !== null} testId="pool-ai-start">
      <span className="sparkle-inline">✦</span> {tp('poolAi.start', candidates.length)}
    </Button>
  );

  const reasonLine =
    reason !== null ? (
      <>
        <p className="card-note ai-card-reason" data-testid="pool-ai-reason">
          {reason}
        </p>
        {unavailable === 'quotaExhausted' ? (
          <Button variant="secondary" icon="credit_card" onClick={() => (canBuyAddOn ? setPurchaseOpen(true) : navigate({ screen: 'settingsPage', page: 'plan' }))} testId="pool-ai-quota-buy">
            {canBuyAddOn ? t('billing.addOnCta') : t('billing.planCta')}
          </Button>
        ) : null}
        {!locked ? <AiBlockFix inspection={props.inspection} reason={unavailable} testId="pool-ai-fix" /> : null}
      </>
    ) : null;

  const overlays = (
    <>
      {reminderOpen ? <PoolNotesReminder missing={missingNotes} total={candidates.length} areasAvailable={areasAvailable} onAddNotes={addNotes} onAnalyze={() => void start()} onClose={() => setReminderOpen(false)} /> : null}
      {purchaseOpen ? <PurchaseSheet mode="addOn" onClose={() => setPurchaseOpen(false)} onPurchased={() => setPurchaseOpen(false)} /> : null}
    </>
  );

  if (props.compact) {
    if (live) {
      return (
        <div className="pool-ai-compact" data-testid={props.testId ?? 'pool-ai-compact'}>
          <PoolAiProgress live={live} areaName={areaName} />
        </div>
      );
    }
    if (candidates.length === 0) {
      return result && props.onShowResult ? (
        <div className="pool-ai-compact" data-testid={props.testId ?? 'pool-ai-compact'}>
          <Button variant="secondary" icon="psychology" onClick={props.onShowResult} testId="pool-ai-show-result">
            {t('poolAi.showResult')}
          </Button>
        </div>
      ) : null;
    }
    return (
      <div className="pool-ai-compact" data-testid={props.testId ?? 'pool-ai-compact'}>
        {startButton}
        {reasonLine}
        {overlays}
      </div>
    );
  }

  const failedAreas = result ? result.areas.filter((a) => a.failure !== null) : [];
  // Read from the stores now, so the card says what the pool and the area pages show.
  const filing = result ? poolAiFiling(result, props.inspection.id, evidenceState, inspectionState, findingState) : { filed: 0, left: 0 };
  const uncertainLeft = result ? poolAiUncertainLeft(result, props.inspection.id, evidenceState, inspectionState, findingState) : 0;
  const areaLines = result ? result.areas.map((outcome) => ({ outcome, found: poolAreaFindings(aiState, evidenceState, inspectionState, findingState, result, outcome.areaId) })) : [];
  const areasWithPhotos = areaLines.filter((line) => line.found.photos > 0).length;

  return (
    <Card tone="low" className="ai-card pool-ai-card" testId={props.testId ?? 'pool-ai-card'}>
      <div className="ai-card-head">
        <span className="icon-tile">
          <span className="sparkle">✦</span>
        </span>
        <div className="ai-card-heading">
          <SectionEyebrow tone="primary">{t('poolAi.eyebrow')}</SectionEyebrow>
          <h3 className="ai-card-title">{t('poolAi.title')}</h3>
        </div>
      </div>

      {live ? <PoolAiProgress live={live} areaName={areaName} /> : null}
      {!live && result && result.failure !== null ? (
        <div className="ai-failed" data-testid="pool-ai-failed" data-code={result.failure}>
          <p className="ai-failed-title">{t('poolAi.failedTitle')}</p>
          <p className="card-note">
            {aiFailureMessage(t, result.failure)} {t('poolAi.failedNothingFiled')}
          </p>
        </div>
      ) : null}
      {!live && !result ? (
        <p className="card-body" data-testid="pool-ai-body">
          {t('poolAi.body')}
        </p>
      ) : null}
      {!live && result && (result.failure === null || result.areas.length > 0) ? (
        <div className="pool-ai-result" data-testid="pool-ai-result" data-filed={filing.filed} data-left={filing.left}>
          {result.failure === null ? <p className="pool-ai-result-title">{t('poolAi.doneTitle')}</p> : null}
          <p className="card-note">{filing.filed > 0 ? tp('poolAi.doneLine', filing.filed, { areas: tp('plan.areasCount', areasWithPhotos) }) : t('poolAi.doneNone')}</p>
          {areaLines.length > 0 ? (
            <div className="pool-index pool-ai-areas" data-testid="pool-ai-areas">
              {areaLines.map(({ outcome, found }) => {
                return (
                  <button
                    type="button"
                    key={outcome.areaId}
                    className="pool-index-row"
                    onClick={() => navigate({ screen: 'areaWorkspace', inspectionId: props.inspection.id, areaId: outcome.areaId, itemSheetId: null })}
                    data-testid="pool-ai-area"
                    data-area-id={outcome.areaId}
                    data-failed={outcome.failure !== null ? 'true' : 'false'}
                  >
                    <span className="pool-index-main">
                      <span className="pool-index-title">{areaName(outcome.areaId)}</span>
                      <span className={`pool-index-sub ${outcome.failure !== null ? 'pool-ai-area-failed' : ''}`}>
                        {outcome.failure !== null
                          ? tp('poolAi.areaFailed', found.photos, { reason: aiFailureMessage(t, outcome.failure) })
                          : t('poolAi.areaLine', { photos: found.photos, rows: found.rows, unclassified: found.unclassified })}
                      </span>
                    </span>
                    <Icon name="chevron_right" className="pool-index-chevron" />
                  </button>
                );
              })}
            </div>
          ) : null}
          {uncertainLeft > 0 ? (
            <p className="card-note" data-testid="pool-ai-uncertain">
              {tp('poolAi.uncertain', uncertainLeft)}
            </p>
          ) : null}
          {filing.left > 0 ? (
            <p className="card-note" data-testid="pool-ai-unplaced">
              {filing.filed > 0 ? `${tp('poolAi.unplaced', filing.left)} ` : null}
              <button type="button" className="pool-ai-link" onClick={props.onAddNotes} data-testid="pool-ai-open-unclassified">
                {t('poolAi.openUnclassified')}
              </button>
            </p>
          ) : null}
          <p className="card-note" data-testid="pool-ai-review-hint">
            {t('poolAi.reviewHint')}
          </p>
          {failedAreas.length > 0 ? (
            <Button variant="secondary" icon="refresh" onClick={() => void retryPoolAreasAction(props.inspection.id)} disabled={policyBlock !== null || locked || !online} testId="pool-ai-retry-areas">
              {tp('poolAi.retryAreas', failedAreas.length)}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!live && (candidates.length > 0 || !result) ? (
        <>
          {candidates.length > 0 && unavailable === null ? <PoolAiEstimate photos={candidates.length} areasAvailable={areasAvailable} testId="pool-ai-estimate" /> : null}
          {candidates.length > 0 && houseFactsMissing && !locked ? (
            <p className="card-note pool-ai-house-facts" data-testid="pool-ai-house-facts">
              {t('poolAi.houseFactsHint')}{' '}
              <button type="button" className="pool-ai-link" onClick={() => navigate({ screen: 'inspectionDetails', inspectionId: props.inspection.id })} data-testid="pool-ai-house-facts-open">
                {t('poolAi.houseFactsOpen')}
              </button>
            </p>
          ) : null}
          {startButton}
          {reasonLine ?? (
            <p className="card-note ai-card-reason" data-testid="pool-ai-privacy">
              {t('ai.privacy')}
            </p>
          )}
        </>
      ) : null}
      {overlays}
    </Card>
  );
}

type Translate = ReturnType<typeof useT>;

// 「约 6 分钟」 / 「不到 1 分钟」.
function durationText(tr: Translate, seconds: number): string {
  return seconds < 60 ? tr.t('poolAi.duration.underMinute') : tr.tp('poolAi.duration.minutes', Math.max(1, Math.round(seconds / 60)));
}

// 「1 分 20 秒」 / 「20 秒」.
function clockText(tr: Translate, seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? tr.t('poolAi.clock.minSec', { m, s }) : tr.t('poolAi.clock.sec', { s });
}

// Before it starts: about how long THESE photos take (src/state/poolAiTiming.ts — the photos, the areas
// they are expected to fill and, once a run was measured here, this phone's own speed). Shown on the card
// and in the note reminder.
function PoolAiEstimate(props: { photos: number; areasAvailable: number; testId: string }) {
  const tr = useT();
  const speed = readPoolAiSpeed();
  const duration = durationText(tr, poolAiEstimateSeconds(props.photos, props.areasAvailable, speed));
  return (
    <p className="card-note pool-ai-estimate" data-testid={props.testId} data-measured={speed.measured ? 'true' : 'false'}>
      {tr.t(speed.measured ? 'poolAi.estimateMeasured' : 'poolAi.estimate', { duration })}
    </p>
  );
}

function PoolAiProgress(props: { live: PoolAiLive; areaName: (areaId: string) => string }) {
  const tr = useT();
  const { t } = tr;
  const { live } = props;
  // A tick every second, so the time and the bar move between two answers.
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const timing = poolAiTiming(live, Date.now());
  // The bar only goes forward within one run, also when a slow answer raises the estimate.
  const shown = useRef({ run: live.startedAt, pct: 0 });
  if (shown.current.run !== live.startedAt) shown.current = { run: live.startedAt, pct: 0 };
  shown.current.pct = Math.max(shown.current.pct, timing.percent);
  const pct = shown.current.pct;
  const timeLine = `${t('poolAi.elapsed', { time: clockText(tr, timing.elapsedSeconds) })} · ${timing.remainingSeconds > 0 ? t('poolAi.remaining', { duration: durationText(tr, timing.remainingSeconds) }) : t('poolAi.overrun')}`;
  const active = live.activeAreaIds.map(props.areaName).filter((name) => name.length > 0);
  const title =
    live.phase === 'sorting'
      ? t('poolAi.sorting', { done: live.sorted, total: live.photos })
      : active.length > 0
        ? tr.tp('poolAi.analyzing', live.areasTotal, { areas: active.join('、'), done: live.areasDone })
        : tr.tp('poolAi.analyzingShort', live.areasTotal, { done: live.areasDone });
  return (
    <div className="import-progress pool-ai-progress" role="status" aria-live="polite" data-testid="pool-ai-progress" data-phase={live.phase}>
      <div className="import-progress-head">
        <span className="ai-progress-spinner" aria-hidden="true" />
        <span className="import-progress-title">{title}</span>
        <span className="import-progress-pct" data-testid="pool-ai-pct">
          {t('common.percent', { n: pct })}
        </span>
      </div>
      <div className="import-progress-track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="import-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="import-progress-hint pool-ai-time" data-testid="pool-ai-time" data-remaining={timing.remainingSeconds}>
        {timeLine}
      </p>
      <p className="import-progress-hint">{t('poolAi.liveHint')}</p>
    </div>
  );
}

// The note reminder before the analysis (Owner 2026-10-05: 「将所有照片添加备注会提高ai分析效果，然后下面两个按钮，
// 现在去添加…直接AI分析」). Tapping outside or Back closes it without starting anything.
function PoolNotesReminder(props: { missing: number; total: number; areasAvailable: number; onAddNotes: () => void; onAnalyze: () => void; onClose: () => void }) {
  const { t, tp } = useT();
  const backdrop = useRef<HTMLDivElement | null>(null);
  useBodyScrollLock();
  useOverlayTouchGuard(backdrop);
  useBackHandler(props.onClose);
  return (
    <Portal>
      <div ref={backdrop} className="sheet-backdrop sheet-backdrop-top" onClick={props.onClose} data-overlay="confirm">
        <section
          className="sheet sheet-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-label={t('poolAi.notesTitle')}
          onClick={(event) => event.stopPropagation()}
          data-scroll-container="true"
          data-testid="pool-ai-notes-reminder"
        >
          <div className="sheet-body">
            <p className="confirm-title">{t('poolAi.notesTitle')}</p>
            <p className="confirm-body">{t('poolAi.notesBody')}</p>
            <p className="confirm-body" data-testid="pool-ai-notes-missing">
              {tp('poolAi.notesMissing', props.missing, { total: props.total })}
            </p>
            <PoolAiEstimate photos={props.total} areasAvailable={props.areasAvailable} testId="pool-ai-notes-estimate" />
            <div className="confirm-actions">
              <Button onClick={props.onAddNotes} testId="pool-ai-add-notes">
                {t('poolAi.notesAddNow')}
              </Button>
              <Button variant="secondary" onClick={props.onAnalyze} testId="pool-ai-analyze-now">
                <span className="sparkle-inline">✦</span> {t('poolAi.notesAnalyzeNow')}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Portal>
  );
}
